
import React from 'react';
import { DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from 'lucide-react'; // Use AlertTriangle for errors
import { RawResponseDisplay } from './RawResponseDisplay';
import { BackendProgressStage } from './enrichmentTypes';

interface EnrichmentErrorDialogContentProps {
  clientName: string;
  error: string; // Main error message derived from progress_stage or error field
  details?: Record<string, unknown> | string | null; // Specific details if available
  onClose: () => void;
  rawResponse?: Record<string, unknown> | unknown[] | null;
  progressStage?: BackendProgressStage | string | null; // Pass progress stage for context
}

export const EnrichmentErrorDialogContent: React.FC<EnrichmentErrorDialogContentProps> = ({
  clientName,
  error,
  details,
  onClose,
  rawResponse,
  progressStage,
}) => {
  let title = "Enrichment Failed";
  if (progressStage === 'error_initiating') {
    title = "Failed to Initiate Enrichment";
  } else if (progressStage === 'error_polling') {
    title = "Error While Polling for Results";
  } else if (progressStage === 'error_processing') {
    title = "Error Processing Enrichment Data";
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Enrichment Error for {clientName}</DialogTitle>
      </DialogHeader>
      <Alert variant="destructive" className="my-4">
        <AlertTriangle className="h-4 w-4" /> {/* Icon for error */}
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          <p>{error}</p>
          {details && <p className="mt-1 text-xs">Details: {typeof details === 'string' ? details : JSON.stringify(details)}</p>}
        </AlertDescription>
      </Alert>
      {rawResponse && (
        (typeof rawResponse === 'object' && rawResponse !== null && (
          (Array.isArray(rawResponse) && rawResponse.length > 0) ||
          (!Array.isArray(rawResponse) && Object.keys(rawResponse).length > 0)
        )) && (
        <RawResponseDisplay
          title="Raw Debug Information (Error)"
          data={rawResponse}
          maxHeight="max-h-60"
        />
      ))}
      <DialogFooter className="mt-4">
        <DialogClose asChild><Button variant="outline" onClick={onClose}>Close</Button></DialogClose>
      </DialogFooter>
    </>
  );
};
